// Offers Configuration
// This file contains all the data for the offers page

export interface TranslatedText {
  en: string;
  lv: string;
  it: string;
}

export interface AdditionalFocusItem {
  text: TranslatedText;
  description?: TranslatedText; // Optional expandable description
  scope?: TranslatedText[]; // Optional scope items
  price?: number; // Price in EUR, optional for items without pricing
}

export interface OfferItem {
  text: TranslatedText;
  description?: TranslatedText;
  bold?: boolean;
  additionalFocusItems?: AdditionalFocusItem[];
}

export interface DeliverableItem {
  text: TranslatedText;
  subitems?: TranslatedText[];
}

export interface OfferConfig {
  id: string;
  title: TranslatedText;
  subtitle: TranslatedText;
  price: string | number; // Can be a number (EUR) or "Custom"
  colorScheme: {
    gradient: string; // Tailwind gradient classes
    primary: string; // Primary color for icons/accents
    button: string; // Button color classes
  };
  borderStyle?: string; // Optional special border styling
  includesPrevious?: string[]; // IDs of previous offers included
  whatWeDo: OfferItem[];
  whatYouGet: DeliverableItem[];
  showInvestmentSection?: boolean; // Whether to show "What you invest" section
}

// Additional Focus Analysis Options
export const ADDITIONAL_FOCUS_ANALYSIS: AdditionalFocusItem[] = [
  {
    text: {
      en: "Risk & Controls analysis",
      lv: "Risku un kontroles analīze",
      it: "Analisi rischi e controlli",
    },
    description: {
      en: "Analysis of operational, financial and compliance risks within the process, including identification of control points to prevent or detect failures.",
      lv: "Operacionālo, finanšu un atbilstības risku analīze procesā, ieskaitot kontroles punktu identificēšanu, lai novērstu vai atklātu kļūmes.",
      it: "Analisi dei rischi operativi, finanziari e di conformità all'interno del processo, inclusa l'identificazione dei punti di controllo per prevenire o rilevare guasti.",
    },
    scope: [
      {
        en: "Identify process risk points and failure scenarios",
        lv: "Identificēt procesa riska punktus un kļūmju scenārijus",
        it: "Identificare i punti di rischio del processo e gli scenari di guasto",
      },
      {
        en: "Define preventive and detective controls",
        lv: "Definēt preventīvās un detektīvās kontroles",
        it: "Definire controlli preventivi e detective",
      },
      {
        en: "Link controls to specific process steps",
        lv: "Saistīt kontroles ar konkrētiem procesa soļiem",
        it: "Collegare i controlli a passaggi specifici del processo",
      },
    ],
    price: 240,
  },
  {
    text: {
      en: "Ownership analysis (RA(S)CI)",
      lv: "Īpašumtiesību analīze (RA(S)CI)",
      it: "Analisi della proprietà (RA(S)CI)",
    },
    description: {
      en: "Clear definition of roles, responsibilities and accountability across steps.",
      lv: "Skaidra lomu, atbildības un pārskatatbildības definīcija visos soļos.",
      it: "Definizione chiara di ruoli, responsabilità e accountability attraverso i passaggi.",
    },
    scope: [
      {
        en: "Define Responsible, Accountable, Consulted, (Supporting) and Informed roles",
        lv: "Definēt atbildīgos, pārskatatbildīgos, konsultētos, (atbalstošos) un informētos",
        it: "Definire ruoli Responsabili, Accountable, Consultati, (Supporto) e Informati",
      },
      {
        en: "Identify ownership gaps and overlaps",
        lv: "Identificēt īpašumtiesību trūkumus un pārklāšanos",
        it: "Identificare lacune e sovrapposizioni di proprietà",
      },
      {
        en: "Resolve unclear handovers between teams",
        lv: "Atrisināt neskaidras nodošanas starp komandām",
        it: "Risolvere passaggi poco chiari tra i team",
      },
    ],
    price: 120,
  },
  {
    text: {
      en: "Process Exceptions & Escalation Design",
      lv: "Procesa izņēmumu un eskalācijas dizains",
      it: "Design delle eccezioni e dell'escalation del processo",
    },
    description: {
      en: "Design of exception handling, escalation paths and response actions when the process does not run as expected.",
      lv: "Izņēmumu apstrādes, eskalācijas ceļu un atbildes darbību dizains, kad process nedarbojas kā paredzēts.",
      it: "Progettazione della gestione delle eccezioni, dei percorsi di escalation e delle azioni di risposta quando il processo non funziona come previsto.",
    },
    scope: [
      {
        en: "Identify \"what can go wrong\" scenarios",
        lv: "Identificēt \"kas var noiet greizi\" scenārijus",
        it: "Identificare scenari \"cosa può andare storto\"",
      },
      {
        en: "Define first-line and second-line response actions",
        lv: "Definēt pirmās un otrās līnijas atbildes darbības",
        it: "Definire azioni di risposta di prima e seconda linea",
      },
      {
        en: "Design escalation and communication paths",
        lv: "Dizainēt eskalācijas un komunikācijas ceļus",
        it: "Progettare percorsi di escalation e comunicazione",
      },
    ],
    price: 240,
  },
  {
    text: {
      en: "System & Integration Analysis",
      lv: "Sistēmu un integrācijas analīze",
      it: "Analisi di sistema e integrazione",
    },
    description: {
      en: "Analysis of systems, data flows and integrations supporting the future-state process.",
      lv: "Sistēmu, datu plūsmu un integrāciju analīze, kas atbalsta nākotnes stāvokļa procesu.",
      it: "Analisi di sistemi, flussi di dati e integrazioni a supporto del processo futuro.",
    },
    scope: [
      {
        en: "Map systems used at each process step",
        lv: "Kartēt sistēmas, kas izmantotas katrā procesa solī",
        it: "Mappare i sistemi utilizzati in ogni passaggio del processo",
      },
      {
        en: "Identify manual handovers and data re-entry points",
        lv: "Identificēt manuālās nodošanas un datu atkārtotas ievades punktus",
        it: "Identificare passaggi manuali e punti di reinserimento dati",
      },
      {
        en: "Define integration and automation touchpoints",
        lv: "Definēt integrācijas un automatizācijas saskares punktus",
        it: "Definire punti di contatto per integrazione e automazione",
      },
    ],
    price: 240,
  },
  {
    text: {
      en: "Data Quality & Digital Readiness Analysis",
      lv: "Datu kvalitātes un digitālās gatavības analīze",
      it: "Analisi qualità dati e prontezza digitale",
    },
    description: {
      en: "Assessment of data quality, data flows and digital readiness across the process to ensure reliable automation and reporting.",
      lv: "Datu kvalitātes, datu plūsmu un digitālās gatavības novērtējums visā procesā, lai nodrošinātu uzticamu automatizāciju un ziņošanu.",
      it: "Valutazione della qualità dei dati, dei flussi di dati e della prontezza digitale attraverso il processo per garantire automazione e reporting affidabili.",
    },
    scope: [
      {
        en: "Identify data inputs, outputs and handovers in the process",
        lv: "Identificēt datu ievades, izvades un nodošanas procesā",
        it: "Identificare input, output e passaggi di dati nel processo",
      },
      {
        en: "Assess data quality (completeness, consistency, ownership)",
        lv: "Novērtēt datu kvalitāti (pilnīgums, konsekvence, īpašumtiesības)",
        it: "Valutare la qualità dei dati (completezza, coerenza, proprietà)",
      },
      {
        en: "Identify manual data handling and duplication",
        lv: "Identificēt manuālo datu apstrādi un dublēšanos",
        it: "Identificare gestione manuale e duplicazione dei dati",
      },
      {
        en: "Define digitalisation and automation prerequisites",
        lv: "Definēt digitalizācijas un automatizācijas priekšnosacījumus",
        it: "Definire prerequisiti per digitalizzazione e automazione",
      },
    ],
    price: 240,
  },
  {
    text: {
      en: "Change Impact Analysis",
      lv: "Izmaiņu ietekmes analīze",
      it: "Analisi impatto cambiamenti",
    },
    description: {
      en: "Evaluation of how the future process will impact people, roles and daily operations.",
      lv: "Novērtējums par to, kā nākotnes process ietekmēs cilvēkus, lomas un ikdienas operācijas.",
      it: "Valutazione di come il processo futuro impatterà persone, ruoli e operazioni quotidiane.",
    },
    scope: [
      {
        en: "Identify changes to roles and responsibilities",
        lv: "Identificēt izmaiņas lomās un atbildībās",
        it: "Identificare cambiamenti a ruoli e responsabilità",
      },
      {
        en: "Assess training and adoption needs",
        lv: "Novērtēt apmācības un pieņemšanas vajadzības",
        it: "Valutare esigenze di formazione e adozione",
      },
      {
        en: "Highlight areas of potential resistance",
        lv: "Izcelt potenciālās pretestības jomas",
        it: "Evidenziare aree di potenziale resistenza",
      },
    ],
    price: 120,
  },
  {
    text: {
      en: "Effort & Repetition Analysis",
      lv: "Pūļu un atkārtošanas analīze",
      it: "Analisi sforzo e ripetizione",
    },
    description: {
      en: "Assessment of manual effort, repetition and workload patterns within the process to identify automation and simplification opportunities.",
      lv: "Manuālo pūļu, atkārtošanas un darba slodzes modeļu novērtējums procesā, lai identificētu automatizācijas un vienkāršošanas iespējas.",
      it: "Valutazione dello sforzo manuale, della ripetizione e dei modelli di carico di lavoro all'interno del processo per identificare opportunità di automazione e semplificazione.",
    },
    scope: [
      {
        en: "Identify highly repetitive process steps",
        lv: "Identificēt ļoti atkārtojošus procesa soļus",
        it: "Identificare passaggi di processo altamente ripetitivi",
      },
      {
        en: "Assess manual effort and workload concentration",
        lv: "Novērtēt manuālos pūliņus un darba slodzes koncentrāciju",
        it: "Valutare sforzo manuale e concentrazione del carico di lavoro",
      },
      {
        en: "Highlight steps sensitive to workload peaks",
        lv: "Izcelt soļus, kas ir jutīgi pret darba slodzes maksimumiem",
        it: "Evidenziare passaggi sensibili ai picchi di carico di lavoro",
      },
      {
        en: "Support prioritisation of automation candidates",
        lv: "Atbalstīt automatizācijas kandidātu prioritizāciju",
        it: "Supportare la prioritizzazione dei candidati all'automazione",
      },
    ],
    price: 120,
  },
  {
    text: {
      en: "Minimum Viable SOP draft",
      lv: "Minimālais dzīvotspējīgais SOP melnraksts",
      it: "Bozza SOP minima vitale",
    },
    description: {
      en: "A lightweight, standardised process document for one critical process, designed to create clarity, consistency and a single source of truth, usable by people, systems and automation initiatives.",
      lv: "Viegls, standartizēts procesa dokuments vienam kritiskam procesam, kas izstrādāts, lai radītu skaidrību, konsekvenci un vienotu patiesības avotu, ko var izmantot cilvēki, sistēmas un automatizācijas iniciatīvas.",
      it: "Un documento di processo leggero e standardizzato per un processo critico, progettato per creare chiarezza, coerenza e un'unica fonte di verità, utilizzabile da persone, sistemi e iniziative di automazione.",
    },
    price: 240,
  },
];

export const OFFERS: OfferConfig[] = [
  {
    id: "diagnosis",
    title: {
      en: "Diagnosis",
      lv: "Diagnoze",
      it: "Diagnosi",
    },
    subtitle:   {
          en: "1 critical process analysis",
          lv: "1 kritiskā procesa analīze",
          it: "1 analisi critica del processo",
        },
    price: 899,
    colorScheme: {
      gradient: "from-blue-500 to-blue-600",
      primary: "blue-600",
      button: "bg-blue-600 hover:bg-blue-700",
    },
    showInvestmentSection: true,
    whatWeDo: [
      {
        text: {
          en: "90min structured interview (based on process maturity assessment models, following)",
          lv: "90min strukturēta intervija (pamatojoties uz procesa brieduma novērtēšanas modeļiem)",
          it: "Intervista strutturata di 90 minuti (basata su modelli di valutazione della maturità dei processi)",
        },
        bold: true,
      },
      {
        text: {
          en: "report creation",
          lv: "Atskaites izveide",
          it: "Creazione report",
        },
      },
      {
        text: {
          en: "30min Q&A on report feedback",
          lv: "30min jautājumi un atbildes par atskaites atsauksmēm",
          it: "30min Q&A sul feedback del report",
        },
      },
      {
        text: {
          en: "30min process optimization intro",
          lv: "30min procesa optimizācijas ievads",
          it: "30min introduzione all'ottimizzazione del processo",
        },
      },
    ],
    whatYouGet: [
      {
        text: {
          en: "1 critical process analysis",
          lv: "1 kritiskā procesa analīze",
          it: "1 analisi critica del processo",
        },
      },
      {
        text: {
          en: "professional consultation analysis report with:",
          lv: "profesionāla konsultācijas analīzes atskaite ar:",
          it: "report di analisi di consulenza professionale con:",
        },
        subitems: [
          {
            en: "high level process map",
            lv: "augsta līmeņa procesa karte",
            it: "mappa di processo ad alto livello",
          },
          {
            en: "process cost evaluation before-after",
            lv: "procesa izmaksu novērtējums pirms-pēc",
            it: "valutazione costi processo prima-dopo",
          },
          {
            en: "emergency treatment (suggestion matrix, top 3 fixes)",
            lv: "ārkārtas ārstēšana (ieteikumu matrica, top 3 labojumi)",
            it: "trattamento d'emergenza (matrice suggerimenti, top 3 correzioni)",
          },
        ],
      },
      {
        text: {
          en: "education",
          lv: "izglītība",
          it: "educazione",
        },
      },
    ],
  },
  {
    id: "prognosis",
    title: {
      en: "Prognosis",
      lv: "Prognoze",
      it: "Prognosi",
    },
    subtitle: {
      en: "1 critical process modeling",
      lv: "1 kritiskā procesa modelēšana",
      it: "1 modellazione critica del processo",
    },
    price: 1999,
    colorScheme: {
      gradient: "from-indigo-500 to-indigo-600",
      primary: "indigo-600",
      button: "bg-indigo-600 hover:bg-indigo-700",
    },
    borderStyle: "border-indigo-500",
    includesPrevious: ["diagnosis"],
    showInvestmentSection: true,
    whatWeDo: [
      {
        text: {
          en: "90min structured interview (based on process maturity assessment models, following)",
          lv: "90min strukturēta intervija (pamatojoties uz procesa brieduma novērtēšanas modeļiem)",
          it: "Intervista strutturata di 90 minuti (basata su modelli di valutazione della maturità dei processi)",
        },
        bold: true,
      },
      {
        text: {
          en: "report creation + draft process design (to-be process high level by analyst + developer)",
          lv: "atskaites izveide + procesa dizaina melnraksts (nākotnes process augstā līmenī no analītiķa + izstrādātāja)",
          it: "creazione report + bozza di design del processo (processo futuro ad alto livello da analista + sviluppatore)",
        },
      },
      {
        text: {
          en: "draft feedback session 60 min finalized process design",
          lv: "melnraksta atsauksmju sesija 60 min finalizēts procesa dizains",
          it: "sessione di feedback bozza 60 min design del processo finalizzato",
        },
      },
      {
        text: {
          en: "implementation costs calculation 30min Q&A on design feedback",
          lv: "ieviešanas izmaksu aprēķins 30min jautājumi un atbildes par dizaina atsauksmēm",
          it: "calcolo costi di implementazione 30min Q&A sul feedback del design",
        },
      },
      {
        text: {
          en: "30min process optimization intro",
          lv: "30min procesa optimizācijas ievads",
          it: "30min introduzione all'ottimizzazione del processo",
        },
      },
    ],
    whatYouGet: [
      {
        text: {
          en: "1 critical process modeling everything in diagnosis",
          lv: "1 kritiskā procesa modelēšana viss diagnozē",
          it: "1 modellazione critica del processo tutto nella diagnosi",
        },
      },
      {
        text: {
          en: "process design:",
          lv: "procesa dizains:",
          it: "design del processo:",
        },
        subitems: [
          {
            en: "detailed process map",
            lv: "detalizēta procesa karte",
            it: "mappa di processo dettagliata",
          },
          {
            en: "documentation",
            lv: "dokumentācija",
            it: "documentazione",
          },
          {
            en: "implementation costs calculation",
            lv: "ieviešanas izmaksu aprēķins",
            it: "calcolo costi di implementazione",
          },
        ],
      },
    ],
  },
  {
    id: "treatment",
    title: {
      en: "Treatment",
      lv: "Ārstēšana",
      it: "Trattamento",
    },
    subtitle: {
      en: "1 critical process implementation",
      lv: "1 kritiskā procesa ieviešana",
      it: "implementazione di 1 processo critico",
    },
    price: "custom",
    colorScheme: {
      gradient: "from-purple-600 to-purple-700",
      primary: "purple-600",
      button: "bg-purple-600 hover:bg-purple-700",
    },
    includesPrevious: ["diagnosis", "prognosis"],
    showInvestmentSection: true,
    whatWeDo: [
      {
        text: {
          en: "90min structured interview (based on process maturity assessment models, following)",
          lv: "90min strukturēta intervija (pamatojoties uz procesa brieduma novērtēšanas modeļiem)",
          it: "Intervista strutturata di 90 minuti (basata su modelli di valutazione della maturità dei processi)",
        },
        bold: true,
      },
      {
        text: {
          en: "report creation + draft process design (to-be process high level by analyst + developer)",
          lv: "atskaites izveide + procesa dizaina melnraksts (nākotnes process augstā līmenī no analītiķa + izstrādātāja)",
          it: "creazione report + bozza di design del processo (processo futuro ad alto livello da analista + sviluppatore)",
        },
      },
      {
        text: {
          en: "draft feedback session 60 min finalized process design",
          lv: "melnraksta atsauksmju sesija 60 min finalizēts procesa dizains",
          it: "sessione di feedback bozza 60 min design del processo finalizzato",
        },
      },
      {
        text: {
          en: "implementation costs calculation 30min Q&A on design feedback optimized process implementation",
          lv: "ieviešanas izmaksu aprēķins 30min jautājumi un atbildes par dizaina atsauksmēm optimizēta procesa ieviešana",
          it: "calcolo costi di implementazione 30min Q&A sul feedback del design implementazione processo ottimizzato",
        },
      },
      {
        text: {
          en: "30min process optimization intro",
          lv: "30min procesa optimizācijas ievads",
          it: "30min introduzione all'ottimizzazione del processo",
        },
      },
    ],
    whatYouGet: [
      {
        text: {
          en: "optimized process implementation",
          lv: "optimizēta procesa ieviešana",
          it: "implementazione processo ottimizzato",
        },
      },
    ],
  },
];

